/**
 * Inline SVG icons — no external fetch, no extra bundle weight.
 * All accept optional `class` props for Tailwind class overrides.
 */

import type { ComponentChildren } from 'preact';

interface IconProps {
  class?: string;
  'aria-hidden'?: boolean;
}

export function ChatBubbleIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.906 1.408 5.51 3.623 7.243L5 22l3.993-1.797A10.787 10.787 0 0 0 12 20.486c5.523 0 10-4.145 10-9.243S17.523 2 12 2Z" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Mirlo brand symbol — 3 orange triangular shapes.
 * Source: mirlo-frontend/src/assets/logos/simbolo-mirlo.svg
 * Used in the "Powered by mirlo" footer; never as a per-customer avatar.
 */
export function MirloLogoIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 311 155"
      fill="#FF681F"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path d="M207.599 0H170.218V96.4568L226.623 154.197H310.397V106.136L207.599 0Z" />
      <path d="M105.468 29.7046H77.0986V96.4567L133.17 154.197H170.218V96.4567L105.468 29.7046Z" />
      <path d="M39.3837 57.7402H0V96.4565L56.0718 154.197H77.0986V96.4565L39.3837 57.7402Z" />
    </svg>
  );
}

/** Bubble tail — user side (right). Mirror horizontally for bot side. */
export function BubbleTailUser(props: IconProps & { fill?: string }) {
  return (
    <svg
      width="11"
      height="15"
      viewBox="0 0 11 15"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path
        d="M0 1C1.6 7.6 5 12.4 10.4 13.7C6.5 14.6 2 14.2 0 14Z"
        fill={props.fill || 'currentColor'}
      />
    </svg>
  );
}

/** Bubble tail — bot side (left). */
export function BubbleTailBot(props: IconProps & { fill?: string }) {
  return (
    <svg
      width="11"
      height="15"
      viewBox="0 0 11 15"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
      style={{ transform: 'scaleX(-1)' }}
    >
      <path
        d="M0 1C1.6 7.6 5 12.4 10.4 13.7C6.5 14.6 2 14.2 0 14Z"
        fill={props.fill || 'currentColor'}
      />
    </svg>
  );
}

/** Double check ticks — message status */
export function TickIcon(
  props: IconProps & { double?: boolean; read?: boolean },
) {
  const color = props.read ? 'var(--w-tick-read)' : 'currentColor';
  return (
    <svg
      width="15"
      height="11"
      viewBox="0 0 15 11"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path
        d="M1 6.2L3.7 8.8L8.3 3.2"
        fill="none"
        stroke={color}
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      {props.double && (
        <path
          d="M5.8 6.2L8.5 8.8L13.1 3.2"
          fill="none"
          stroke={color}
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      )}
    </svg>
  );
}

/** Verified badge check */
export function VerifiedBadge(props: IconProps) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <circle cx="7" cy="7" r="7" fill="white" />
      <path
        d="M4 7.2L6 9.2L10 4.8"
        fill="none"
        stroke="var(--w-hbg)"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

/** Sun icon for theme toggle */
export function SunIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

/** Moon icon for theme toggle */
export function MoonIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export function SendIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4 20-7Z" />
    </svg>
  );
}

/**
 * Attachment icons, one per media kind. Same 24x24 stroked style as SendIcon
 * so they sit consistently inside the attachment card.
 */
function StrokedIcon(props: IconProps & { children: ComponentChildren }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden={props['aria-hidden'] ?? true}
    >
      {props.children}
    </svg>
  );
}

export function ImageFileIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-4.5-4.5L3 21" />
    </StrokedIcon>
  );
}

export function VideoFileIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <rect x="2" y="5" width="14" height="14" rx="2" />
      <path d="m16 11 6-4v10l-6-4Z" />
    </StrokedIcon>
  );
}

export function AudioFileIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="16" cy="16" r="3" />
    </StrokedIcon>
  );
}

export function DocumentFileIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </StrokedIcon>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </StrokedIcon>
  );
}

export function PaperclipIcon(props: IconProps) {
  return (
    <StrokedIcon {...props}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </StrokedIcon>
  );
}
